import { Component, inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { ToastService } from '../../../../shared/services/toast/toast.service';

interface MediaFile {
  caption: string;
  data: string;
  url: string;
}

export interface FilePreviewDialogData {
  media: MediaFile;
}

/** Renders as an actual MatDialog (not an inline `position: fixed` template block) so it
 * gets the same CDK-overlay-managed z-index as every other dialog in the app — an inline
 * fixed overlay nested inside `MediaFileListComponent`'s own MatDialog got trapped inside
 * that dialog's stacking context and could be rendered over by an unrelated dialog opened
 * afterwards (e.g. Settings > Users), since CDK overlays are stacked in open-order, not by
 * a component-local z-index. */
@Component({
    selector: 'app-file-preview-dialog',
    templateUrl: './file-preview-dialog.component.html',
    standalone: true,
    imports: [MatIconModule]
})
export class FilePreviewDialogComponent {
    private dialogRef = inject<MatDialogRef<FilePreviewDialogComponent>>(MatDialogRef);
    private sanitizer = inject(DomSanitizer);
    private http = inject(HttpClient);
    private toastService = inject(ToastService);
    data = inject<FilePreviewDialogData>(MAT_DIALOG_DATA);

    previewIsEmbeddable = false;
    previewEmbedUrl: SafeResourceUrl | null = null;
    media: MediaFile;

    constructor() {
        this.media = this.data.media;

        // Only ever bypass the sanitizer for an https URL — media.url is persisted file
        // metadata (S3-backed), so a javascript:/data: URI here would execute in the app's
        // origin once bound to the iframe [src] below.
        let isHttpsUrl: boolean;
        try {
            isHttpsUrl = new URL(this.media.url, window.location.origin).protocol === 'https:';
        } catch {
            isHttpsUrl = false;
        }
        if (!isHttpsUrl) {
            return;
        }

        const ext = (this.media.caption ?? '').split('.').pop()?.toLowerCase() ?? '';
        if (ext === 'pdf') {
            this.previewIsEmbeddable = true;
            this.previewEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.media.url);
        } else if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx'].includes(ext)) {
            this.previewIsEmbeddable = true;
            this.previewEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
                `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(this.media.url)}`
            );
        }
    }

    close(): void {
        this.dialogRef.close();
    }

    downloadFile(): void {
        this.http.get(this.media.url, { responseType: 'blob' }).subscribe({
            next: (blob) => {
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                a.download = this.media.data.split('/').pop();
                a.click();
                URL.revokeObjectURL(objectUrl);
            },
            error: () => {
                this.toastService.showError('Error downloading file');
            }
        });
    }
}
